<template>
	<div class="client-filter-modal">
		<app-loader
			v-if="isLoading"
			:style="loaderStyle"
		/>
		<div class="client-filter-modal__search">
			<app-input
				ref="$input"
				v-model="filter"
				class="client-filter-modal__search-input"
				:placeholder="$t('search_short')"
				show-search-button
			/>
			<button
				class="client-filter-modal__close"
				@click="emit('close')"
			>
				<app-icon name="close" width="20px" color="#979797" />
			</button>
		</div>
		<div class="client-filter-modal__header">
			<div class="client-filter-modal__header-title">
				{{ $t('clients_label') }}
			</div>
		</div>
		<div
			class="client-filter-modal__body"
			ref="$body"
		>
			<div
				class="client-filter-modal__body-item"
				data-filter-client-id="-1"
				data-filter-union="0"
			>
				<app-checkbox
					:style="{ height: '16px', marginRight: '8px' }"
					:value="store.byBase"
					text=""
					@input="onToggleByBase"
				/>
				<div
					class="client-filter-modal__body-item-text"
					@click="onToggleByBase(!store.byBase)"
				>
					{{ $t('search_by_base') }}
				</div>
			</div>

			<template
				v-for="opt in filteredOptions"
				:key="opt.Id"
			>
				<div
					class="client-filter-modal__body-item"
					:data-filter-client-id="opt.Id"
					:data-filter-union="opt.Union.length ? 1 : 0"
				>
					<app-checkbox
						:style="{ height: '16px', marginRight: '8px' }"
						:value="isOptChecked(opt)"
						text=""
						@input="(on) => onToggleClient(opt, on)"
					/>
					<div
						class="client-filter-modal__body-item-text"
						@click="onToggleClient(opt, !isOptChecked(opt))"
					>
						{{ opt.Name }}<span
							v-if="opt.Union.length"
							class="client-filter-modal__body-item-holding"
						>&nbsp;{{ $t('with_holding_suffix') }}</span>
					</div>
				</div>
				<div
					v-for="member in opt.Union"
					:key="member.Id"
					class="client-filter-modal__body-item client-filter-modal__body-item--union"
					:data-filter-client-id="member.Id"
					data-filter-union="0"
				>
					<app-checkbox
						:style="{ height: '16px', marginRight: '8px' }"
						:value="isMemberChecked(opt, member.Id)"
						text=""
						@input="(on) => onToggleUnion(opt, member, on)"
					/>
					<div
						class="client-filter-modal__body-item-text"
						@click="onToggleUnion(opt, member, !isMemberChecked(opt, member.Id))"
					>
						{{ member.Name }}
					</div>
				</div>
			</template>
		</div>
	</div>
</template>

<script setup lang="ts">
import {
	computed,
	nextTick,
	onMounted,
	ref,
	watch
} from "vue";
import AppCheckbox from "@/components/ui-kit/checkbox/AppCheckbox.vue";
import AppInput from "@/components/ui-kit/input/AppInput.vue";
import AppLoader from "@/components/common/AppLoader.vue";
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";
import {
	searchStore,
	ClientFilterOption,
	ClientFilterUnion
} from "@/store/search";

const props = defineProps<{
	focusClient?: { Id: number, UseUnion: boolean } | null,
}>()

const emit = defineEmits(['change', 'close'])

const HIGHLIGHT_CLASS = 'client-filter-modal__body-item--highlight'

const $input = ref<typeof AppInput | null>(null)
const $body = ref<HTMLDivElement | null>(null)

const store = searchStore()

const filter = ref('')

const isLoading = computed(() => store.isClientFilterLoading)

// Верхнеуровневая строка отмечена, когда выбран «холдинг целиком»; у клиента без холдинга
// такая запись хранится с UseUnion=false, потому что холдинга у неё нет.
const isOptChecked = (opt: ClientFilterOption) =>
	store.selectedClients.some(c => c.Id === opt.Id && c.UseUnion === (opt.Union.length > 0))

// Вложенный отмечен и сам по себе, и когда опция схлопнута в «холдинг целиком».
const isMemberChecked = (opt: ClientFilterOption, memberId: number) =>
	store.selectedClients.some(c => c.Id === memberId && !c.UseUnion)
	|| store.selectedClients.some(c => c.Id === opt.Id && c.UseUnion)

const filteredOptions = computed<ClientFilterOption[]>(() => {
	const query = filter.value.trim().toLowerCase()
	if (!query) {
		return store.clientFilterList
	}

	return store.clientFilterList.filter(opt =>
		opt.Name?.toLowerCase().includes(query)
		|| opt.Union.some(m => m.Name?.toLowerCase().includes(query))
	)
})

const loaderStyle = computed(() => ({
	'background-color': 'rgba(255, 255, 255, 0.5)'
}))

const onToggleByBase = (on: boolean) => {
	store.setByBase(on)
	emit('change')
}

const onToggleClient = (opt: ClientFilterOption, on: boolean) => {
	store.toggleFilterOption(opt, on)
	emit('change')
}

const onToggleUnion = (opt: ClientFilterOption, member: ClientFilterUnion, on: boolean) => {
	store.toggleFilterMember(opt, member, on)
	emit('change')
}

const findRow = (req: { Id: number, UseUnion: boolean }) => {
	const container = $body.value
	if (!container) {
		return null
	}
	// один Id может встречаться дважды: верхнеуровневая строка «с холдингом» и вложенный
	// участник холдинга — поэтому ищем по паре атрибутов, а по одному Id только как фолбэк
	const exact = container.querySelector<HTMLElement>(
		`[data-filter-client-id="${req.Id}"][data-filter-union="${req.UseUnion ? 1 : 0}"]`
	)
	return exact ?? container.querySelector<HTMLElement>(`[data-filter-client-id="${req.Id}"]`)
}

const focusRow = async () => {
	const req = props.focusClient
	if (!req) {
		return
	}
	await nextTick()
	let el = findRow(req)
	if (!el && filter.value) {
		filter.value = ''
		await nextTick()
		el = findRow(req)
	}
	const container = $body.value
	if (!el || !container) {
		return
	}

	const cRect = container.getBoundingClientRect()
	const eRect = el.getBoundingClientRect()
	container.scrollTop += (eRect.top - cRect.top) - (container.clientHeight - el.clientHeight) / 2

	const target = el
	target.classList.remove(HIGHLIGHT_CLASS)
	target.getBoundingClientRect()
	target.classList.add(HIGHLIGHT_CLASS)
	const onEnd = () => {
		target.classList.remove(HIGHLIGHT_CLASS)
		target.removeEventListener('animationend', onEnd)
	}
	target.addEventListener('animationend', onEnd)
}

watch(() => props.focusClient, focusRow)

onMounted(async () => {
	await nextTick(() => $input.value?.focusInput())
	focusRow()
})
</script>

<style scoped lang="scss">
.client-filter-modal {
	display: flex;
	flex-direction: column;
	position: relative;
	flex: 1;
	min-height: 0;
	padding: 12px 0 0 12px;

	&__search {
		display: flex;
		gap: 8px;
		width: 100%;
		margin-bottom: 8px;
		padding-right: 12px;

		&-input {
			width: 100%;
		}
	}

	&__close {
		display: none;
		flex-shrink: 0;
		justify-content: flex-end;
		width: 32px;
		height: 32px;
		padding: 0;
		border: 0;
		background: transparent;
		cursor: pointer;
	}

	&__header {
		display: flex;
		align-items: center;
		justify-content: space-between;
		margin-bottom: 8px;
		padding-right: 12px;
		color: #979797;

		&-title {
			font-size: 12px;
			line-height: 16px;
			font-weight: 600;
		}
	}

	&__body {
		display: flex;
		flex: 1;
		min-height: 0;
		flex-direction: column;
		gap: 8px;
		overflow: auto;
		padding-right: 12px;
		padding-bottom: 12px;

		&-item {
			display: flex;
			align-items: center;
			font-size: 14px;
			line-height: 18px;
			color: #333;
			cursor: pointer;
			transition: color 0.1s;

			&--union {
				margin-left: 24px;
			}

			&--highlight {
				border-radius: 4px;
				animation: clientFilterHighlightBlink 2s ease-in-out;
			}

			&-text {
				font-size: 14px;
				line-height: 18px;
			}

			&-holding {
				color: #979797;
			}

			&:hover {
				text-decoration: underline;
				color: #396742;
			}
		}
	}
}

@media (max-width: 480px) {
	.client-filter-modal__close {
		display: flex;
	}
}

@keyframes clientFilterHighlightBlink {
	0%, 33.33%, 66.66%, 100% {
		background-color: transparent;
	}
	16.66%, 50%, 83.33% {
		background-color: #F3F8F4;
	}
}
</style>
